import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: 'default' | 'outlined' | 'elevated' | 'flat';
	padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
	radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
	children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({
	variant = 'default',
	padding = 'md',
	radius = 'lg',
	className = '',
	children,
	...props
}) => {
	// Variant styles using clean utility classes
	const variantStyles = {
		default: `bg-white border border-gray-200`,
		outlined: `bg-white border-2 border-neutral-300`,
		elevated: `bg-white shadow-lg border border-neutral-100`,
		flat: `bg-neutral-50`,
	};

	// Padding styles using standard Tailwind classes
	const paddingStyles = {
		none: '',
		sm: 'p-3',
		md: 'p-6',
		lg: 'p-8',
		xl: 'p-12',
	};

	// Radius styles using standard Tailwind classes
	const radiusStyles = {
		none: '',
		sm: 'rounded-sm',
		md: 'rounded-md',
		lg: 'rounded-lg',
		xl: 'rounded-xl',
		full: 'rounded-full',
	};

	return (
		<div
			className={`
        font-primary
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${radiusStyles[radius]}
        ${className}
      `
				.trim()
				.replace(/\s+/g, ' ')}
			{...props}
		>
			{children}
		</div>
	);
};

export default Card;

// Convenience components for specific use cases
export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`pb-4 border-b border-neutral-200 mb-6 font-primary ${className}`}
		{...props}
	>
		{children}
	</div>
);

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div className={`font-primary ${className}`} {...props}>
		{children}
	</div>
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`pt-4 border-t border-neutral-200 mt-6 font-primary ${className}`}
		{...props}
	>
		{children}
	</div>
);
